import { Injectable } from '@angular/core';
import { ConfigSessionService } from './interfaces/config-session.service';
import { Observable, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { UtilitiesService } from '../../utilities/services/utilities.service';


@Injectable({
  providedIn: 'root'
})
export class SessionService {
  /**
   * @property sessionId - Identificador de la sesión del usuario
   */
  private sessionId: string | null = null;
  /**
   * @property sessionOb - Observer de cambios en la sesión
   */
  private sessionOb: Subject<boolean> = new Subject<boolean>();
  /**
   * @property sessionChange$ - Manejador del observer de cambios
   */
  private sessionChange$: Observable<boolean> = this.sessionOb.asObservable();
  /**
   * @property isAdmin
   */
  private isAdmin: boolean | null;
  /**
   * @property activateAdminCookie
   */
  private activateAdminCookie: boolean;
  /**
   * @method cookieName
   */
  private cookieName: string;

  /**
   * Crea el servicio de sesión
   * #constructor
   * @param cookieService
   * @param utilitiesService
   * @param configSession
   */
  constructor(
    private configSession: ConfigSessionService,
    private cookieService: CookieService,
    private utilitiesService: UtilitiesService
  ) {
    this.updateSession();
    this.setConfigCookie(this.configSession);
  }

  /**
   * Limpia la sesión del usuario
   * @method clearSession
   */
  clearSession() {
    this.cookieService.delete(this.cookieName, '/');
    this.sessionId = null;
    this.isAdmin = null;
    // Activa la suscripción en el header para cambiar a usuario desconectado
    this.sessionOb.next(true);
  }

  /**
   * Comprueba si existe una sesión de usuario
   * @method isActiveSession
   * #returns {boolean}
   */
  isActiveSession(): boolean {
    this.updateSession();
    return !!this.sessionId;
  }

  /**
   * Retorna el tipo de usuario para quien esté conectado
   * @method isAdminUser
   * #returns {boolean}
   */
  isAdminUser() {
    return this.isAdmin;
  }

  /**
   * Retorna el identificador de sesión del usuario
   * @method getSessionId
   */
  getSessionId(): any {
    return this.sessionId;
  }

  /**
   * Regresa el manejador del observer de sesión
   * @method observableSession
   * #returns {Observable<boolean>}
   */
  observableSession(): Observable<boolean> {
    return this.sessionChange$;
  }

  /**
   * @method setActivateAdminCookie
   * @param config
   */
  setConfigCookie(config: ConfigSessionService) {
    this.activateAdminCookie = config.activateAdminCookie;
    this.cookieName = config.cookieName;
  }

  /**
   * Setea el identificador de la sesión
   * @method setSessionId
   * @param sessionId
   * @param isAdminUser
   */
  setSessionId(sessionId: string, isAdminUser: boolean) {
    const admin = isAdminUser ? 1 : 0;
    this.cookieService.set(
      this.cookieName,
      this.activateAdminCookie ? admin + sessionId : sessionId,
      0,
      '/'
    );
    this.updateSession();
    // Activa la suscripción en le header para cambiar a usuario conectado
    this.sessionOb.next(true);
  }

  /**
   * Actualiza la sesión en memoria
   * @method updateSession
   */
  updateSession() {
    if (this.activateAdminCookie) {
      const indicatorAdmin = this.cookieService.get(this.cookieName)[0];
      this.sessionId = this.cookieService.get(this.cookieName).slice(1) || null;
      this.isAdmin = Boolean(
        this.utilitiesService.stringToNumber(indicatorAdmin)
      );
    } else {
      this.sessionId = this.cookieService.get(this.cookieName);
    }
  }

}
